import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ExternalLink, Info, Pencil, Plus, Trash2, Check } from "lucide-react";

export default function NotamPage() {
  const [codes, setCodes] = useState([]);
  const [managing, setManaging] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [newName, setNewName] = useState("");

  useEffect(() => {
    api.get("/settings/notam-ycodes").then((r) => setCodes(r.data.codes || [])).catch(() => {});
  }, []);

  const persist = async (list) => {
    setCodes(list);
    try { await api.put("/settings/notam-ycodes", { codes: list }); }
    catch { toast.error("Could not save Y-codes"); }
  };
  const updateAt = (i, key, val) => setCodes((cs) => cs.map((c, idx) => (idx === i ? { ...c, [key]: val } : c)));
  const removeAt = (i) => persist(codes.filter((_, idx) => idx !== i));
  const add = () => {
    const code = newCode.trim().toUpperCase();
    if (!code) return;
    persist([...codes, { code, name: newName.trim() }]);
    setNewCode(""); setNewName("");
  };
  const saveEdits = () => { persist(codes.filter((c) => c.code.trim())); setManaging(false); };

  return (
    <div className="space-y-6 fade-up max-w-5xl">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-lime-600 mb-1">Notices to Airmen</p>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading flex items-center gap-2">
          <AlertTriangle className="h-8 w-8 text-lime-500" /> NOTAM
        </h1>
        <p className="text-slate-500 mt-1">Check active NOTAMs before every flight via the NAIPS Active NOTAM Directory.</p>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
        <Info className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
        <div className="text-sm text-slate-700">
          <p className="font-medium text-slate-900">How to retrieve NOTAMs</p>
          <p className="mt-1">Australian NOTAMs are served by Airservices via NAIPS (no public REST API — SOAP with a registered account). In NAIPS: <strong>NOTAM → Active NOTAM Directory → select group → enter Y-Code → Retrieve</strong>. Use the quick codes below, then open NAIPS to pull the current directory. The pilot is responsible for verifying NOTAM currency.</p>
        </div>
      </div>

      <a href="https://www.airservicesaustralia.com/naips/" target="_blank" rel="noopener noreferrer" data-testid="notam-open-naips"
        className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-5 py-2.5 rounded-lg font-medium transition-all">
        Open NAIPS Active NOTAM Directory <ExternalLink className="h-4 w-4" />
      </a>

      <section className="bg-white border border-slate-200 rounded-xl p-6">
        <div className="flex items-center justify-between gap-4 mb-4">
          <h2 className="text-lg font-semibold font-heading text-slate-900">Quick Y-Code reference</h2>
          {managing ? (
            <Button size="sm" onClick={saveEdits} data-testid="ycode-done-btn" className="bg-slate-900 hover:bg-slate-800 text-white"><Check className="h-4 w-4 mr-1.5" /> Done</Button>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setManaging(true)} data-testid="ycode-edit-btn"><Pencil className="h-4 w-4 mr-1.5" /> Edit</Button>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {codes.map((y, i) => (
            <div key={i} data-testid={`ycode-${y.code}`} className="p-3 rounded-lg border border-slate-200 text-center hover:border-lime-300 transition-all relative">
              {managing ? (
                <>
                  <Input value={y.code} onChange={(e) => updateAt(i, "code", e.target.value.toUpperCase())} data-testid={`ycode-code-input-${i}`} className="h-8 text-center font-mono font-bold mb-1" placeholder="YXXX" />
                  <Input value={y.name} onChange={(e) => updateAt(i, "name", e.target.value)} data-testid={`ycode-name-input-${i}`} className="h-7 text-center text-xs" placeholder="Location" />
                  <button onClick={() => removeAt(i)} data-testid={`ycode-remove-${i}`} className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600"><Trash2 className="h-3 w-3" /></button>
                </>
              ) : (
                <>
                  <p className="font-mono font-bold text-slate-900">{y.code}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{y.name}</p>
                </>
              )}
            </div>
          ))}
          {codes.length === 0 && !managing && <p className="text-sm text-slate-400 col-span-full">No Y-codes. Click Edit to add some.</p>}
        </div>

        {managing && (
          <div className="mt-4 flex flex-wrap items-end gap-2 border-t border-slate-100 pt-4">
            <div>
              <label className="text-xs font-medium text-slate-500">Y-Code</label>
              <Input value={newCode} onChange={(e) => setNewCode(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && add()} data-testid="ycode-new-code" placeholder="YHBA" className="h-9 w-28 font-mono" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Location</label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} data-testid="ycode-new-name" placeholder="Hervey Bay" className="h-9 w-44" />
            </div>
            <Button onClick={add} data-testid="ycode-add-btn" className="h-9 bg-lime-400 hover:bg-lime-300 text-slate-900"><Plus className="h-4 w-4 mr-1.5" /> Add</Button>
          </div>
        )}
      </section>

      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-slate-700">
        <p className="font-medium text-red-800">Pre-flight reminder</p>
        <p className="mt-1">Always review NOTAMs for temporary restricted areas (TRAs), aerodrome works, emergency operations and drone-relevant hazards before launch. Combine with your CASA airspace check and NAIPS area briefing.</p>
      </div>
    </div>
  );
}
